import { Order, OrderSummary } from '../../types/order';

/**
 * User Orders Types
 * Centralized type definitions for all user orders hooks
 */

// =============================================================================
// Base Types
// =============================================================================

export interface UserOrderStats {
  total_orders: number;
  pending_orders: number;
  preparing_orders: number;
  on_the_way_orders: number;
  completed_orders: number;
  total_spent: number;
}

export interface UserOrderState {
  orders: Order[];
  ordersSummary: OrderSummary[];
  stats: UserOrderStats;
  loading: boolean;
  error: string | null;
  hasInitialized: boolean;
}

// =============================================================================
// Hook Return Types
// =============================================================================

export interface UseUserOrderStateReturn {
  orders: Order[];
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
  ordersSummary: OrderSummary[];
  setOrdersSummary: React.Dispatch<React.SetStateAction<OrderSummary[]>>;
  stats: UserOrderStats;
  setStats: React.Dispatch<React.SetStateAction<UserOrderStats>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  clearError: () => void;
}

export interface UseUserOrderDataReturn {
  fetchUserOrders: () => Promise<void>;
  fetchOrdersSummary: (limit?: number) => Promise<void>;
  fetchOrderDetail: (orderId: number) => Promise<Order | null>;
  refreshData: () => void;
}

export interface UseUserOrderStatsReturn {
  stats: UserOrderStats;
  fetchOrderStats: () => Promise<void>;
  getCompletionRate: () => number;
  getTotalSpent: () => number;
  getMostRecentOrderStatus: () => string;
}

export interface UseUserOrderInitReturn {
  hasInitialized: boolean;
  setHasInitialized: React.Dispatch<React.SetStateAction<boolean>>;
  initializeUserOrders: () => void;
}

export interface UseUserOrdersReturn {
  orders: Order[];
  ordersSummary: OrderSummary[];
  stats: UserOrderStats;
  loading: boolean;
  error: string | null;
  fetchUserOrders: () => Promise<void>;
  fetchOrdersSummary: (limit?: number) => Promise<void>;
  fetchOrderStats: () => Promise<void>;
  fetchOrderDetail: (orderId: number) => Promise<Order | null>;
  refreshData: () => void;
}

// =============================================================================
// Configuration Types
// =============================================================================

export interface UserOrderConfig {
  autoRefresh?: boolean;
  refreshInterval?: number;
  defaultSummaryLimit?: number;
  enableStats?: boolean;
}

// =============================================================================
// Default Values
// =============================================================================

export const defaultUserOrderStats: UserOrderStats = {
  total_orders: 0,
  pending_orders: 0,
  preparing_orders: 0,
  on_the_way_orders: 0,
  completed_orders: 0,
  total_spent: 0,
};

export const defaultUserOrderState: UserOrderState = {
  orders: [],
  ordersSummary: [],
  stats: defaultUserOrderStats,
  loading: false,
  error: null,
  hasInitialized: false,
};

export const defaultUserOrderConfig: UserOrderConfig = {
  autoRefresh: false,
  refreshInterval: 30000,
  defaultSummaryLimit: 5,
  enableStats: true,
};
